// O esqueleto do webmail: barra superior, pastas à esquerda, conteúdo à
// direita. A janela de escrita flutua por cima de tudo.

import { useCallback, useState } from "react";
import { Outlet, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { paraEncaminhar, paraResponder } from "./citar";
import type { ModoDeResposta } from "./Leitura";
import { useCaixa, useSessao } from "../sessao/contexto";
import type { Mensagem } from "../api/tipos";
import { BarraLateral } from "./BarraLateral";
import { BarraSuperior } from "./BarraSuperior";
import { Escrever, type Rascunho } from "./Escrever";
import { useAtalhos } from "./atalhos";
import { Icone } from "../ui/Icone";
import { useCelular } from "../ui/tela";

export function Layout() {
  const [recolhida, setRecolhida] = useState(false);
  const [gaveta, setGaveta] = useState(false);
  const celular = useCelular();
  const [rascunho, setRascunho] = useState<Rascunho | null>(null);
  const [recado, setRecado] = useState("");
  const navegar = useNavigate();
  const eu = useCaixa();
  const { pasta = "INBOX", uuid } = useParams();
  const [parametros] = useSearchParams();

  const escrever = useCallback(() => setRascunho({ modo: "novo" }), []);

  /**
   * Abre a janela de escrita JÁ PREENCHIDA.
   *
   * Existe para o cancelamento de um envio programado: o servidor devolve o
   * texto, e "editar" é reabrir a janela com ele. Fica no Layout porque é
   * ele que tem a janela — uma segunda janela de escrita dentro da tela de
   * Programados seria um segundo lugar para montar a mesma mensagem, com as
   * validações e o limite de anexo divergindo do primeiro.
   */
  const escreverCom = useCallback(
    (campos: Omit<Rascunho, "modo">) => setRascunho({ modo: "novo", ...campos }),
    [],
  );

  // Preenche destinatário, assunto e citação a partir da mensagem aberta.
  // Antes só o uuid era passado, e a janela abria EM BRANCO — o que não era
  // só incômodo: o contrato exige ao menos um destinatário, então responder
  // falhava se o endereço não fosse redigitado à mão.
  const responder = useCallback(
    (modo: ModoDeResposta, mensagem: Mensagem) => {
      const campos =
        modo === "encaminhar"
          ? paraEncaminhar(mensagem)
          : paraResponder(mensagem, eu.caixa ?? "", modo === "responder_todos");
      setRascunho({
        modo: modo === "encaminhar" ? "encaminhar" : "responder",
        origem: mensagem.uuid,
        ...campos,
      });
    },
    [eu.caixa],
  );

  // Os atalhos que dependem de uma mensagem aberta ficam na tela de leitura;
  // aqui vivem os globais. A janela de escrita desativa todos: ela é um campo
  // de texto inteiro, e o atalho seria roubado de quem está digitando.
  useAtalhos(
    {
      escrever,
      voltar: uuid ? () => navegar(`/correio/${encodeURIComponent(pasta)}`) : undefined,
    },
    rascunho === null,
  );

  return (
    // `100dvh` e não `h-screen`: no celular a `vh` do CSS conta a barra do
    // navegador que some ao rolar, então o rodapé da aplicação ficava embaixo
    // dela. A `dvh` acompanha a altura que de fato existe.
    //
    // `overflow-hidden` É A GARANTIA, não enfeite.
    //
    // Apontamento 15 da revisão de UX: "ao rolar até o fim, a lateral e o
    // fundo acabam e sobra uma faixa branca vazia embaixo". Esse é o sintoma
    // exato de o DOCUMENTO rolar atrás de um invólucro de altura fixa: o
    // invólucro para em 100dvh, e abaixo dele aparece o fundo do `body`.
    //
    // A estrutura dependia de cada tela filha se comportar — ser um item de
    // flex com rolagem própria. Lista, Leitura, Busca e Contatos faziam;
    // "Não recebi" e "Meu e-mail chegou?" não, e eram as duas que
    // transbordavam. Consertar as duas conserta as duas; isto aqui conserta
    // a CLASSE. Com `overflow-hidden` na raiz, uma tela nova que esqueça a
    // própria rolagem não consegue mais fazer o documento crescer.
    <div className="h-[100dvh] overflow-hidden flex flex-col bg-linha text-tinta">
      <FaixaDePersonificacao />
      <BarraSuperior
        area="correio"
        // Um botão, DUAS ideias — e por isso ele escolhe qual mexer. Mexendo
        // nas duas, a primeira abertura no celular herdava a coluna estreita
        // do desktop e a gaveta vinha só com ícones.
        recolher={() =>
          celular ? setGaveta((g) => !g) : setRecolhida((r) => !r)
        }
        buscaInicial={parametros.get("q") ?? ""}
      />

      <div className="flex flex-1 min-h-0">
        <BarraLateral
          recolhida={recolhida}
          aberta={gaveta}
          aoFechar={() => setGaveta(false)}
          aoEscrever={escrever}
        />
        {/* A ÁREA DE CONTEÚDO É QUEM ROLA — "lateral fixa e só a área de
            conteúdo rola", que é o ajuste pedido, escrito uma vez aqui em
            vez de repetido em cada tela.
            A tela que tem rolagem própria (`flex-1 overflow-y-auto`) tem a
            altura limitada por esta coluna, então esta barra de rolagem
            nunca aparece e não há duas. A tela que NÃO tem ganha rolagem
            aqui, em vez de ser cortada. */}
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          {/* A coluna de conteúdo rola por dentro; a resposta encaixada é
              item de flex DEPOIS dela, então a mensagem respondida fica
              acima e visível. Daí o `overflow-hidden` aqui e a rolagem no
              item de dentro: com `overflow-y-auto` nesta div, a resposta
              encaixada rolaria junto com a mensagem e sairia da tela. */}
          <div className="flex min-w-0 flex-1 flex-col overflow-y-auto">
            <Outlet context={{ responder, escreverCom }} />
          </div>

          {/* A RESPOSTA ABRE ABAIXO DA MENSAGEM, não por cima dela.
              Apontamento 47. `origem` é o que distingue: resposta e
              encaminhamento têm uma mensagem na tela para não cobrir;
              mensagem nova não tem, e continua flutuante no canto — uma
              barra de 60% de altura por cima da lista de mensagens seria
              pior que a janela. */}
          {rascunho?.origem && (
            <Escrever
              encaixada
              inicial={rascunho}
              aoFechar={() => setRascunho(null)}
              aoEnviar={(aviso) => {
                setRecado(aviso);
                setTimeout(() => setRecado(""), 5000);
              }}
            />
          )}
        </div>
      </div>

      {/* ESCREVER, no celular.
          O botão original vive na gaveta, e escrever é a ação mais frequente
          do webmail — deixá-la atrás de um menu é pedir dois toques para o
          que se faz o dia inteiro. Some quando a janela de escrita está
          aberta: ali ele não teria função e cobriria o campo de texto. */}
      {!rascunho && (
        <button
          onClick={escrever}
          aria-label="Escrever"
          className="md:hidden fixed bottom-5 right-5 z-30 flex h-14 w-14 items-center justify-center rounded-2xl bg-acao text-white shadow-lg active:scale-95 transition-transform"
        >
          <Icone nome="Pencil" tamanho={22} />
        </button>
      )}

      {/* A flutuante, para mensagem NOVA. A encaixada está logo acima,
          dentro da coluna de conteúdo. */}
      {rascunho && !rascunho.origem && (
        <Escrever
          inicial={rascunho}
          aoFechar={() => setRascunho(null)}
          aoEnviar={(aviso) => {
            setRecado(aviso);
            setTimeout(() => setRecado(""), 5000);
          }}
        />
      )}

      {recado && (
        <div
          role="status"
          className="fixed bottom-6 left-6 flex items-center gap-4 rounded-lg bg-tinta px-4 py-3 text-sm text-white shadow-xl z-50"
        >
          <Icone nome="CheckCircle2" tamanho={16} className="text-acao-clara" />
          {recado}
        </div>
      )}
    </div>
  );
}


/**
 * A FAIXA DE "VOCÊ NÃO É ESTA PESSOA".
 *
 * Aparece quando um administrador entrou na caixa de outra pessoa, e precisa
 * ser impossível de não ver. O risco que ela existe para evitar não é
 * técnico: é alguém esquecer em qual caixa está e escrever um e-mail que sai
 * no nome do cliente, para um terceiro que não tem como saber que não foi
 * ele. Nada no servidor evita isso — só esta faixa.
 *
 * Por isso ela é VERMELHA e fica no topo de tudo, acima da barra: amarelo
 * lê-se como "atenção", e isto não é atenção, é "você está em outro lugar".
 * E fica FORA da área que rola, para não poder sair da vista.
 *
 * Em sessão normal não desenha nada — nem um espaço.
 */
function FaixaDePersonificacao() {
  const { sessao, voltarAoPainel } = useSessao();
  const navegar = useNavigate();
  if (!sessao?.personificada_por) return null;

  // VOLTAR É VOLTAR PARA ONDE SE ESTAVA.
  //
  // Antes isto só trocava a sessão, e a pessoa caía em /correio/INBOX — como
  // se tivesse acabado de entrar na própria conta, em vez de voltar ao
  // painel de onde saiu. Relatado em 26/10/2026.
  //
  // O ponto de partida é gravado ao ENTRAR na caixa (ver EntrarNaCaixa), em
  // sessionStorage, que é por aba. O padrão existe para o caso de a pessoa
  // ter recarregado a página no meio: aí não há de onde voltar, e
  // /painel/caixas é o lugar de onde quase sempre se entra numa caixa.
  async function sairEVoltar() {
    let destino = "/painel/caixas";
    try {
      destino = sessionStorage.getItem("cdw_voltar_para") || destino;
      sessionStorage.removeItem("cdw_voltar_para");
    } catch {
      // Armazenamento bloqueado: vai para o padrão.
    }
    await voltarAoPainel();
    navegar(destino);
  }

  return (
    <div
      role="alert"
      className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-perigo px-4 py-2 text-center text-sm text-white"
    >
      <span>
        Você está na caixa de <strong>{sessao.caixa}</strong>. Tudo que for
        enviado daqui sai no nome dessa pessoa.
      </span>
      <button
        type="button"
        onClick={() => void sairEVoltar()}
        className="rounded-full bg-white/20 px-3 py-1 text-xs font-medium underline-offset-2 hover:bg-white/30"
      >
        Sair e voltar ao painel
      </button>
    </div>
  );
}
